// App-wide background tasks: work that outlives the dialog that started it
// (today, licensing a Getty image into the library). A reactive singleton like
// `toastStore`; `@components/taskList.svelte` draws each task as a floating card
// in the top-right corner, above the toasts.
//
// A task reports a status line and, when the step can measure itself, a
// `progress` fraction (null draws an indeterminate bar). It ends `done` (the
// card lingers briefly, then goes) or `error` (the card stays, with whatever
// actions the owner offers, until dismissed).

export type TaskState = 'running' | 'done' | 'error';

export type TaskAction = { label: string; run: () => void };

export type Task = {
  id: number;
  title: string;
  // A small image beside the text, when the task is about one.
  thumb?: string;
  status: string;
  progress: number | null;
  state: TaskState;
  actions: TaskAction[];
};

const DONE_LINGER_MS = 4000;

export class TaskHandle {
  constructor(
    private store: TaskStore,
    readonly id: number
  ) {}

  #task() {
    return this.store.tasks.find((t) => t.id === this.id);
  }

  // A new step (or progress within one). Also revives a failed task, which is
  // how a retry reuses its card.
  update(status: string, progress: number | null = null) {
    const t = this.#task();
    if (!t) return;
    t.status = status;
    t.progress = progress == null ? null : Math.max(0, Math.min(1, progress));
    t.state = 'running';
    t.actions = [];
  }

  done(status: string) {
    const t = this.#task();
    if (!t) return;
    t.status = status;
    t.progress = 1;
    t.state = 'done';
    t.actions = [];
    setTimeout(() => this.dismiss(), DONE_LINGER_MS);
  }

  fail(status: string, actions: TaskAction[] = []) {
    const t = this.#task();
    if (!t) return;
    t.status = status;
    t.progress = null;
    t.state = 'error';
    t.actions = actions;
  }

  dismiss() {
    this.store.remove(this.id);
  }
}

class TaskStore {
  tasks = $state<Task[]>([]);

  #nextId = 0;

  // Whether anything is still working: a page unload would cut it off.
  get running(): boolean {
    return this.tasks.some((t) => t.state === 'running');
  }

  start(title: string, status: string, thumb?: string): TaskHandle {
    const id = this.#nextId++;
    this.tasks.push({ id, title, thumb, status, progress: null, state: 'running', actions: [] });
    return new TaskHandle(this, id);
  }

  remove(id: number) {
    this.tasks = this.tasks.filter((t) => t.id !== id);
  }
}

export const taskStore = new TaskStore();
